import { Button, Css, RouteTabWithContent, ScrollableParent, TabContent, Tabs } from "@homebound/beam";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import {
  createDevelopmentLotSummaryReleaseUrl,
  createProjectDashboardUrl,
  createReleaseToTradesConfirmUrl,
  createReleaseToTradesReviewUrl,
} from "src/RouteUrls";
import { ConfirmTradesStep } from "src/routes/developments/lot-summary/lot-release/ConfirmTradesStep";
import {
  ReleaseToTradesPage_ProjectFragment,
  useReleaseToTradesPageQuery,
} from "src/routes/developments/lot-summary/lot-release/ReleaseToTradesPage.generated";
import { LotConfigParams, releaseToTradesPaths } from "src/routes/routesDef";
import { queryResult } from "src/utils";
import { ConfirmTradesStepV2 } from "./ConfirmTradesStepV2";
import { ReviewOrdersStep } from "./ReviewOrdersStep";
import { SubHeader } from "./components/SubHeader";

export function ReleaseToTradesPage() {
  const { projectId } = useParams<LotConfigParams>();
  const query = useReleaseToTradesPageQuery({ variables: { id: projectId! }, skip: !projectId });
  return queryResult(query, (data) => <ReleaseToTradesView project={data.project} />);
}

type ReleaseToTradesViewProps = { project: ReleaseToTradesPage_ProjectFragment };

function ReleaseToTradesView({ project }: ReleaseToTradesViewProps) {
  const isConfirmPage = !!useRouteMatch([releaseToTradesPaths.confirm]);
  const title = isConfirmPage
    ? "Confirm trades and select line items to release"
    : "Review purchase orders to be released";
  const tabs: RouteTabWithContent[] = [
    {
      name: "Confirm Trades",
      href: createReleaseToTradesConfirmUrl(project.id),
      path: releaseToTradesPaths.confirm,
      render: () => (project.enableBidPackagesV2 ? <ConfirmTradesStepV2 /> : <ConfirmTradesStep />),
    },
    {
      name: "Release POs",
      href: createReleaseToTradesReviewUrl(project.id),
      path: releaseToTradesPaths.review,
      render: () => <ReviewOrdersStep />,
    },
  ];

  return (
    <>
      <div css={Css.bgWhite.$}>
        <div css={Css.px8.$}>
          <SubHeader preHeader={project.name}>{title}</SubHeader>
          <Tabs tabs={tabs} />
        </div>
      </div>
      {project.enableBidPackagesV2 ? (
        // V2 steps use ScrollableContent/ScrollableFooter, which portal into the nearest
        // ScrollableParent. The parent must wrap TabContent so the step has a shared scroll
        // container (for the virtualized table) and a pinned footer slot (for the action bar).
        <ScrollableParent>
          <TabContent tabs={tabs} />
        </ScrollableParent>
      ) : (
        <div css={Css.pbPx(100).$}>
          <TabContent tabs={tabs} />
        </div>
      )}
    </>
  );
}

type ExitButtonProps = { developmentId: string | undefined; projectId: string };

export function ExitButton({ developmentId, projectId }: ExitButtonProps) {
  const history = useHistory();
  return (
    <Button
      size="lg"
      variant="quaternary"
      label="Exit"
      // Redirect to either the Development Lot Summary or the Project page
      onClick={() =>
        developmentId
          ? history.push(createDevelopmentLotSummaryReleaseUrl(developmentId))
          : history.push(createProjectDashboardUrl(projectId))
      }
    />
  );
}
